/*
 * kaching-debug.js — GemPages variant picker -> Kaching Bundles sync
 * ---------------------------------------------------------------------------
 * PROBLEM
 *   On /products/luhxe the GemPages variant picker is bound to a DIFFERENT
 *   product than the Kaching Bundles block:
 *
 *     page product (Kaching block) : "luhxe"                          7423426887727
 *     GemPages picker product      : "luhxe-travel-bag-2-0-new-offer" 8350471094319
 *
 *   Both products carry the same options (Color: Rose/Charcoal/Snow,
 *   Model: Classic/Roller Edition) but completely different variant IDs.
 *
 *   Kaching syncs itself to the theme by watching the add-to-cart form's
 *   [name="id"] value (and ?variant= in the URL) and then calling
 *   _handleNativeVariantChange(id). That handler bails out unless the id
 *   exists in ITS product:  this._product.variants.find(v => v.id == id).
 *   GemPages writes 8350471094319's variant ids into the form, none of which
 *   exist in 7423426887727 -> the handler no-ops -> the bundle bars keep
 *   showing the colour/model the page was first rendered with.
 *
 * FIX
 *   Ignore variant ids and resolve the picker's OPTION VALUES against the
 *   product payload Kaching itself rendered, then write the matching id to
 *
 *     <kaching-bundles-block current-variant-id="...">
 *
 *   which is the exact attribute _handleNativeVariantChange sets. The block
 *   re-renders every deal bar and its prices in one pass, so the page behaves
 *   the same as on a natively supported theme.
 *
 *   Do NOT do this by clicking the per-item dropdowns inside the bars. Each bar
 *   is a <label> wrapping that bar's radio input, so any click inside it selects
 *   that bundle: driving the dropdowns makes "Buy 1" and "Buy 2" visibly open
 *   and swap selection on every variant change.
 *
 * DIRECTION GUARD
 *   Kaching (and the GemPages custom JS on the block) pushes its own selection
 *   back into the picker by calling .click() on the swatches. Syncing on those
 *   would make the two fight each other — picking a colour for item 2 of the
 *   2-pack would immediately bounce back onto every item — so we only react to
 *   a real click on a visible swatch and quietly record everything else.
 *
 *   Debug logging: localStorage.setItem('kb-sync-debug', '1')
 */
(function () {
  'use strict';

  var SETTLE_MS = 200;   // let GemPages repaint its swatches before we read them

  var DEBUG = false;
  try { DEBUG = window.localStorage.getItem('kb-sync-debug') === '1'; } catch (e) {}

  function log() {
    if (!DEBUG || !window.console) return;
    try { console.log.apply(console, ['[kb-sync]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function all(sel, root) {
    return [].slice.call((root || document).querySelectorAll(sel));
  }

  function closest(node, sel) {
    while (node && node.nodeType === 1) {
      if (node.matches && node.matches(sel)) return node;
      node = node.parentElement;
    }
    return null;
  }

  /* ------------------------------------------------------- GemPages picker */

  // Option values the picker currently has selected, keyed by option name.
  // GemPages exposes both on the active swatch: option-data + option-value.
  function pickerState() {
    var active = all('.option-item-active .option-value-wrapper[option-value]');
    var state = null;

    for (var i = 0; i < active.length; i++) {
      var name = active[i].getAttribute('option-data');
      var value = active[i].getAttribute('option-value');
      if (!name || value === null) continue;
      if (!state) state = {};
      state[name] = value;
    }
    return state;
  }

  function changed(before, after) {
    if (!after) return false;
    if (!before) return true;
    for (var name in after) {
      if (after.hasOwnProperty(name) && before[name] !== after[name]) return true;
    }
    return false;
  }

  /* --------------------------------------------------------- Kaching block */

  function blocks() {
    return all('kaching-bundles-block');
  }

  // The product payload Kaching rendered for this block. The ids have to come
  // from Kaching's own copy — the theme's product data is a different product.
  function kachingProduct(block) {
    var host = closest(block, 'kaching-bundle');
    var pid = host && host.getAttribute('product-id');
    var scripts = all('script.kaching-bundles-product');
    var fallback = null;

    for (var i = 0; i < scripts.length; i++) {
      var data;
      try { data = JSON.parse(scripts[i].textContent); } catch (e) { continue; }
      if (!data || !data.variants || !data.variants.length) continue;
      if (pid && String(scripts[i].getAttribute('data-product-id')) === String(pid)) return data;
      if (!fallback && scripts[i].hasAttribute('data-main')) fallback = data;
    }
    return fallback;
  }

  // variant.options is positional, so resolve option name -> position first.
  function findVariant(product, state) {
    var names = [];
    var declared = product.options || [];
    for (var d = 0; d < declared.length; d++) {
      var position = declared[d].position || (d + 1);
      names[position - 1] = declared[d].name || declared[d].defaultName;
    }
    if (!names.length) return null;

    for (var v = 0; v < product.variants.length; v++) {
      var options = product.variants[v].options || [];
      var matched = 0;
      var ok = true;
      for (var i = 0; i < options.length; i++) {
        var name = names[i];
        if (!name) { ok = false; break; }              // cannot verify — do not guess
        if (state[name] === undefined) continue;       // option not in the picker
        if (state[name] !== options[i]) { ok = false; break; }
        matched++;
      }
      if (ok && matched) return product.variants[v];
    }
    return null;
  }

  function bundleItems() {
    return all('.kaching-bundles__bar .kaching-bundles__bar-variant');
  }

  // What every bundle item currently shows. Returns null if the items disagree,
  // i.e. the shopper has picked different variants inside the bundle.
  function bundleState() {
    var items = bundleItems();
    if (!items.length) return null;

    var state = null;
    for (var i = 0; i < items.length; i++) {
      var options = all('.kaching-bundles-option[data-name]', items[i]);
      var current = {};
      for (var j = 0; j < options.length; j++) {
        current[options[j].getAttribute('data-name')] = options[j].getAttribute('data-value');
      }
      if (!state) { state = current; continue; }
      for (var name in current) {
        if (current.hasOwnProperty(name) && state[name] !== current[name]) return null;
      }
    }
    return state;
  }

  /* -------------------------------------------------------------- the sync */

  var known = null;             // last picker state we have accounted for
  var userSyncPending = false;  // a shopper picker click is being processed
  var settleTimer = null;

  function apply(state) {
    var list = blocks();
    var applied = false;

    for (var i = 0; i < list.length; i++) {
      var product = kachingProduct(list[i]);
      if (!product) continue;

      var variant = findVariant(product, state);
      if (!variant) { log('no variant for', state); continue; }
      if (String(list[i].getAttribute('current-variant-id')) === String(variant.id)) { applied = true; continue; }

      log('current-variant-id ->', variant.id, state);
      list[i].setAttribute('current-variant-id', String(variant.id));
      applied = true;
    }
    return applied;
  }

  function scheduleSync(reason) {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      settleTimer = null;
      var state = pickerState();
      if (state) {
        if (changed(known, state)) {
          log('picker changed (' + reason + ')', state);
          apply(state);
        }
        known = state;
      }
      userSyncPending = false;
    }, SETTLE_MS);
  }

  // Kaching writing its own selection into the picker is not a shopper change:
  // record the new value so the next real change diffs against it, but do not
  // push anything back — that is what would flatten per-item choices.
  function absorb() {
    if (userSyncPending) return;
    var state = pickerState();
    if (!state) return;
    if (changed(known, state)) log('absorb', state);
    known = state;
  }

  function scheduleAbsorb() {
    var delays = [0, 150, 400, 900, 1600];
    for (var i = 0; i < delays.length; i++) setTimeout(absorb, delays[i]);
  }

  /* ------------------------------------------------------------- listeners */

  function insideKaching(node) {
    return !!closest(node, 'kaching-bundle, kaching-bundles-block, .kaching-bundles, .kaching-bundles__block, .kaching-bundles__swatch-dropdown__options');
  }

  function onEvent(event) {
    var target = event.target;
    if (!target || target.nodeType !== 1) return;

    var inKaching = insideKaching(target);
    var inPicker = !inKaching &&
      !!closest(target, '.option-value-wrapper[option-value], .variant-option-group, [variant-option-name]');
    if (!inKaching && !inPicker) return;

    // A shopper picking a value in the GemPages picker — the one case we act on.
    //
    // The test is deliberately narrow. Each GemPages swatch is a <label> around
    // the visible .option-value-wrapper plus a visually hidden radio input, so a
    // programmatic .click() from Kaching gets turned into a label activation and
    // the browser fires a **trusted** `change` on that hidden input. Reacting to
    // that would bounce Kaching's own per-item choice back onto every item.
    // Only a real click landing on the visible swatch counts.
    var act = event.type === 'click' &&
      event.isTrusted !== false &&
      inPicker &&
      !!closest(target, '.option-value-wrapper[option-value]');

    log('event', event.type, 'trusted=' + (event.isTrusted !== false),
        'kaching=' + inKaching, 'picker=' + inPicker, 'act=' + act);

    if (act) {
      userSyncPending = true;
      scheduleSync('picker-click');
      return;
    }
    scheduleAbsorb();
  }

  /* ----------------------------------------------------------------- boot */

  // Runs once the picker AND the rendered bars are both present. Aligns the two
  // if the page came up out of step — e.g. the shopper clicked a swatch while
  // the block was still mounting, so nothing was listening yet.
  //
  // Only aligns while every bundle item still agrees; per-item choices the
  // shopper has already made are never overwritten.
  function boot() {
    var state = pickerState();
    if (!state) return false;
    if (!bundleItems().length) return false;   // bars not rendered yet — keep waiting

    var bundle = bundleState();
    if (bundle && changed(bundle, state)) {
      log('aligning bundle on load', state);
      apply(state);
    }
    known = state;
    return true;
  }

  function start() {
    if (!document.querySelector('kaching-bundle, script.kaching-bundles-product')) return;

    document.addEventListener('click', onEvent, true);
    document.addEventListener('change', onEvent, true);

    // The block mounts asynchronously, and its bars render a beat after that.
    window.addEventListener('kaching-bundles-initialized', boot);
    var tries = 0;
    var timer = setInterval(function () {
      if (boot() || ++tries > 80) clearInterval(timer);   // give it up to 20s
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
